import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Lightbulb, Shield, Zap, ArrowRight, Info, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";
import DhanConnectModal from "@/components/DhanConnectModal";
import PlaceStrategyModal from "@/components/PlaceStrategyModal";

/**
 * Strategy Ideas — setup-matched options strategies for the underlying.
 *
 * Renders 1-3 ranked strategy cards based on the latest AI signal trend +
 * Options Lab context (VIX, IV, DTE). Each card shows the legs (BUY/SELL +
 * strike + LTP), full payoff math (max profit/loss/breakeven), and a direct
 * "Place via DHAN" 1-click execution button.
 *
 * Strategies are EDUCATIONAL IDEAS, not investment advice — disclaimer surfaced
 * prominently per SEBI guidance (we don't claim to be an RIA).
 */
function fmtINR(n) {
  if (n === null || n === undefined || n === "unlimited") return n === "unlimited" ? "Unlimited" : "—";
  if (typeof n !== "number") return String(n);
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function riskIcon(profile) {
  if (profile?.includes("undefined")) return <Zap className="h-3.5 w-3.5 text-[#F59E0B]" />;
  return <Shield className="h-3.5 w-3.5 text-[#22C55E]" />;
}

function StrategyCard({ s, ctx, rank, isBest, isSelected, onSelect, dhanConnected, onPlaceDhan, onConnectDhan }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect?.(); } }}
      className={`border p-4 space-y-3 cursor-pointer transition-colors ${
        isSelected
          ? "border-[#007AFF] bg-[#007AFF]/10 shadow-[0_0_0_1px_rgba(0,122,255,0.4)]"
          : "border-zinc-800 bg-[#0a0a0a] hover:border-zinc-600"
      }`}
      data-testid={`strategy-card-${rank}`}
      data-selected={isSelected ? "true" : "false"}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {riskIcon(s.risk_profile)}
            <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{s.tag}</span>
          </div>
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white">{s.name}</h3>
        </div>
        {isBest && (
          <span className="text-[9px] uppercase tracking-widest font-mono px-2 py-0.5 bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/40 flex-shrink-0">
            Best fit
          </span>
        )}
      </div>

      {/* Legs */}
      <div className="space-y-1.5">
        {s.legs.map((leg, i) => (
          <div key={i} className="flex items-center justify-between text-xs font-mono py-1.5 px-2 bg-black/40 border border-zinc-900">
            <div className="flex items-center gap-2">
              <span className={`font-bold uppercase tracking-widest ${leg.action === "BUY" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                {leg.action}
              </span>
              <span className="text-zinc-300">{leg.strike} {leg.type}</span>
              <span className="text-zinc-600">×{leg.qty_lots} lot{leg.qty_lots > 1 ? "s" : ""}</span>
            </div>
            <span className="text-zinc-300 font-bold">₹{leg.ltp.toFixed(2)}</span>
          </div>
        ))}
      </div>

      {/* Payoff math */}
      <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
        <div className="bg-[#0a0a0a] p-2">
          <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Max profit</div>
          <div className="text-sm font-bold text-[#22C55E]">{fmtINR(s.max_profit)}</div>
        </div>
        <div className="bg-[#0a0a0a] p-2">
          <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Max loss</div>
          <div className="text-sm font-bold text-[#EF4444]">{fmtINR(s.max_loss)}</div>
        </div>
        <div className="bg-[#0a0a0a] p-2">
          <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Breakeven</div>
          <div className="text-sm font-bold text-zinc-200">
            {s.breakeven.map((b) => b.toLocaleString("en-IN")).join(" / ")}
          </div>
        </div>
        <div className="bg-[#0a0a0a] p-2">
          <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Margin / capital</div>
          <div className="text-sm font-bold text-zinc-200">{fmtINR(s.margin_required)}</div>
        </div>
      </div>

      {/* Probability + rationale */}
      <div className="flex items-center gap-3 text-[10px] font-mono">
        <span className="text-zinc-500 uppercase tracking-widest">PoP</span>
        <span className="text-[#007AFF] font-bold">{s.probability_of_profit}%</span>
        <div className="ml-auto h-1 flex-1 bg-zinc-900 overflow-hidden">
          <div className="h-full bg-[#007AFF]" style={{ width: `${s.probability_of_profit}%` }} />
        </div>
      </div>

      <p className="text-xs text-zinc-400 leading-relaxed">{s.rationale}</p>

      {/* CTA — DHAN one-click "place all legs" (live order placement). */}
      <div className="space-y-1.5">
        {dhanConnected ? (
          <button
            onClick={(e) => { e.stopPropagation(); onPlaceDhan?.(s); }}
            className="w-full text-xs uppercase tracking-widest px-3 py-2.5 border-2 border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] font-mono font-bold flex items-center justify-center gap-2"
            data-testid={`strategy-place-dhan-${rank}`}
          >
            <Zap className="h-3.5 w-3.5" /> Place via DHAN · 1-click
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onConnectDhan?.(); }}
            className="w-full text-xs uppercase tracking-widest px-3 py-2 border border-[#22C55E]/40 bg-[#22C55E]/5 hover:bg-[#22C55E]/15 text-[#22C55E] font-mono flex items-center justify-center gap-2"
            data-testid={`strategy-connect-dhan-${rank}`}
          >
            <Link2 className="h-3 w-3" /> Connect DHAN for 1-click execution
          </button>
        )}
      </div>
    </div>
  );
}

export default function StrategyIdeas({ symbol, expiry }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [dhanStatus, setDhanStatus] = useState(null);
  const [dhanConnectOpen, setDhanConnectOpen] = useState(false);
  const [placeStrategy, setPlaceStrategy] = useState(null); // strategy obj when modal open

  const refreshDhan = useCallback(async () => {
    try {
      const { data } = await api.get("/dhan/status");
      setDhanStatus(data);
    } catch {
      setDhanStatus({ connected: false });
    }
  }, []);

  useEffect(() => { refreshDhan(); }, [refreshDhan]);

  const load = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ symbol });
      if (expiry) params.set("expiry", expiry);
      const { data } = await api.get(`/options-lab/strategies?${params.toString()}`);
      setData(data);
      setSelectedIdx(0); // Default-select the top-ranked strategy on fresh load.
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load strategy ideas");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [symbol, expiry]);

  useEffect(() => { load(); }, [load]);

  const strategies = data?.strategies || [];
  const ctx = data?.context || {};

  return (
    <div className="space-y-3" data-testid="strategy-ideas-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-[#F59E0B]" />
            <h2 className="font-heading font-black text-xl uppercase tracking-tight">Strategy Ideas</h2>
          </div>
          {ctx.trend && (
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mt-1">
              Setup: <span className={ctx.trend === "bullish" ? "text-[#22C55E]" : ctx.trend === "bearish" ? "text-[#EF4444]" : "text-zinc-400"}>{ctx.trend}</span>
              {" · "}{ctx.probability}% prob
              {" · "}VIX {ctx.india_vix?.toFixed(2) || "—"}
              {" · DTE "}{ctx.dte ?? "—"}d
              {" · Lot "}{ctx.lot_size}
            </div>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && !data && (
        <div className="border border-zinc-800 bg-[#0a0a0a] p-8 flex items-center justify-center text-zinc-500 text-xs font-mono uppercase tracking-widest">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Picking strategies…
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-3 text-xs text-zinc-400" data-testid="strategy-ideas-error">
          {error}
        </div>
      )}

      {/* Empty state — connected but no strategies returned (e.g. neutral signal + low VIX) */}
      {!loading && !error && strategies.length === 0 && (
        <div className="border border-zinc-800 bg-[#0a0a0a] p-6 text-center text-xs text-zinc-500" data-testid="strategy-ideas-empty">
          <Info className="h-4 w-4 mx-auto mb-2 text-zinc-600" />
          No strategies match this regime right now. The engine prefers to skip rather than recommend a marginal setup.
        </div>
      )}

      {/* Cards */}
      {strategies.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {strategies.map((s, i) => (
            <StrategyCard
              key={`${s.name}-${i}`}
              s={s}
              ctx={{ ...ctx, symbol }}
              rank={i}
              isBest={i === 0}
              isSelected={i === selectedIdx}
              onSelect={() => setSelectedIdx(i)}
              dhanConnected={!!dhanStatus?.connected}
              onPlaceDhan={(strat) => setPlaceStrategy(strat)}
              onConnectDhan={() => setDhanConnectOpen(true)}
            />
          ))}
        </div>
      )}

      {/* Disclaimer — mandatory per SEBI guidance */}
      <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-600 leading-relaxed border-l-2 border-zinc-800 pl-3 py-1">
        Strategy IDEAS for educational context · not investment advice · you make your own decision.
        Margins are estimates — confirm in DHAN before placing.
      </div>

      {/* DHAN connect & place modals */}
      <DhanConnectModal
        open={dhanConnectOpen}
        onClose={() => setDhanConnectOpen(false)}
        onConnected={refreshDhan}
      />
      <PlaceStrategyModal
        open={!!placeStrategy}
        strategy={placeStrategy}
        ctx={ctx}
        symbol={symbol}
        expiry={expiry}
        onClose={() => setPlaceStrategy(null)}
      />
    </div>
  );
}
